/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { OfficeFinderListItem } from '@gh/builder-shared';
import {
  ReportRohcAreaDto,
  ReportRohcDetailsDto,
  ReportRohcMasterCostCenterDto,
  ReportRohcOfficeCostCenterDto,
  ReportRohcOfficeDto,
} from '@gh/builder-shared-data';
import { createLayoutComparatorFactory } from '@gh/common-internal-shared';
import {
  composeSetters,
  createGroupIndexDriver,
  createOrderIndexDriver,
  createTableDriver,
  entityComplementor,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  isDirtyTypeNew,
  setEntityField,
  setUpdatedDirtyType,
  Table,
  whenExisting,
} from '@gh/common-shared';
import { AutocompleteItem, DirtyType, ID, WithDirtyType, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { FocusedCell, GridLayout } from '@gh/core-ui-grid';
import { arrayIterator, cast, composeComparators, EMPTY_ARRAY, maybeMap, valueComparator } from '@gh/core-util';
import { first, flatMap, omit, stubTrue, uniqueId } from 'lodash';

export enum ReportRohcDetailsTab { Areas = 'AREAS', CostCenters = 'COST_CENTERS' }

export type ReportRohcDetails = {
  areas: Table<ReportRohcArea>;
  areaOffices: Table<ReportRohcOffice>;
  masterCostCenters: Table<ReportRohcMasterCc>;
  officeCostCenters: Table<ReportRohcOfficeCc>;
  selectedAreaId?: ID;
  officeFocusedCell?: FocusedCell;
  selectedMasterCcId?: ID;
  selectedTab: ReportRohcDetailsTab;
  layouts: Hash<GridLayout>;
};
export type ReportRohcArea = WithVersion<WithDirtyType<{
  id: ID;
  name: string;
  description: string;
}>>;
export type ReportRohcOffice = WithEntityState<ReportRohcOfficeDto & { areaId: ID }>;

export type ReportRohcMasterCc = WithVersion<WithDirtyType<{
  id: ID;
  code: number;
  name: string;
}>>;
export type ReportRohcOfficeCc = ReportRohcOfficeCostCenterDto & { masterId: ID };

export const getReportRohcAreaId = (area: ReportRohcArea) => area.id;
export const getReportRohcOfficeId = (office: ReportRohcOffice) => office.officeId;
export const getReportRohcMasterCcId = (cc: ReportRohcMasterCc) => cc.id;
export const getReportRohcOfficeCcId = (cc: ReportRohcOfficeCc) => cc.costCenterId;

export const getReportRohcOfficeAreaId = (office: ReportRohcOffice) => office.areaId;
export const getReportRohcOfficeCcMasterId = (cc: ReportRohcOfficeCc) => cc.masterId;

export const getReportRohcOfficeCatalogId = (office: ReportRohcOffice) => office.officeId;

export const newReportRohcArea = (): ReportRohcArea => ({
  id: uniqueId('rohc-area:'),
  name: '',
  description: '',
  dirtyType: DirtyType.New,
});

export const newReportRohcMasterCc = (): ReportRohcMasterCc => ({
  id: uniqueId('rohc-master-cc:'),
  code: <any>void 0,
  name: '',
  dirtyType: DirtyType.New,
});

const REPORT_ROHC_OFFICE_ENTITY_CONFIG: EntityConfig<ReportRohcOffice> = {
  housePlan: {
    required: true,
  },
};

export const reportRohcOfficeFieldState = fieldStateByEntityConfig(REPORT_ROHC_OFFICE_ENTITY_CONFIG);
export const validateReportRohcOfficeField = fieldValidatorByEntityConfig(REPORT_ROHC_OFFICE_ENTITY_CONFIG);
export const validateReportRohcOffice = entityValidatorByEntityConfig(REPORT_ROHC_OFFICE_ENTITY_CONFIG);
export const complementReportRohcOffice = entityComplementor(validateReportRohcOffice);

export const setReportRohcOfficeField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateReportRohcOfficeField));

const reportRohcAreaComparator = composeComparators<ReportRohcArea>(
  valueComparator('name'),
  valueComparator(getReportRohcAreaId));
const reportRohcOfficeComparator = composeComparators<ReportRohcOffice>(
  valueComparator('name'),
  valueComparator(getReportRohcOfficeId));
const reportRohcMasterCcComparator = composeComparators<ReportRohcMasterCc>(
  valueComparator('code'),
  valueComparator(getReportRohcMasterCcId));
const reportRohcOfficeCcComparator = composeComparators<ReportRohcOfficeCc>(
  valueComparator('code'),
  valueComparator(getReportRohcOfficeCcId));

export const reportRohcAreaLayoutComparatorFactory = createLayoutComparatorFactory(
  reportRohcAreaComparator,
  valueComparator(getReportRohcAreaId));
export const reportRohcMasterCcLayoutComparatorFactory = createLayoutComparatorFactory(
  reportRohcMasterCcComparator,
  valueComparator(getReportRohcMasterCcId));

export const reportRohcAreaTable = createTableDriver(
  getReportRohcAreaId,
  createOrderIndexDriver(
    'name',
    getReportRohcAreaId,
    whenExisting,
    reportRohcAreaComparator));
export const reportRohcOfficeTable = createTableDriver(
  getReportRohcOfficeId,
  createGroupIndexDriver(
    'area',
    getReportRohcOfficeId,
    whenExisting,
    (office) => office.areaId,
    reportRohcOfficeComparator),
  createGroupIndexDriver(
    'area-all',
    getReportRohcOfficeId,
    stubTrue,
    (office) => office.areaId,
    reportRohcOfficeComparator));
export const reportRohcMasterCcTable = createTableDriver(
  getReportRohcMasterCcId,
  createOrderIndexDriver(
    'code',
    getReportRohcMasterCcId,
    whenExisting,
    reportRohcMasterCcComparator));
export const reportRohcOfficeCcTable = createTableDriver(
  getReportRohcOfficeCcId,
  createGroupIndexDriver(
    'master',
    getReportRohcOfficeCcId,
    whenExisting,
    (cc) => cc.masterId,
    reportRohcOfficeCcComparator),
  createGroupIndexDriver(
    'master-all',
    getReportRohcOfficeCcId,
    stubTrue,
    (cc) => cc.masterId,
    reportRohcOfficeCcComparator),
  createGroupIndexDriver(
    'office',
    getReportRohcOfficeCcId,
    stubTrue,
    (cc) => cc.officeId,
    reportRohcOfficeCcComparator));

export const REPORT_ROHC_DETAILS_INIT: ReportRohcDetails = {
  areas: reportRohcAreaTable.create(),
  areaOffices: reportRohcOfficeTable.create(),
  masterCostCenters: reportRohcMasterCcTable.create(),
  officeCostCenters: reportRohcOfficeCcTable.create(),
  selectedTab: ReportRohcDetailsTab.Areas,
  layouts: {},
};

export const reportRohcOfficeFromDto = (area: ReportRohcAreaDto, office: ReportRohcOfficeDto): ReportRohcOffice => ({
  ...office,
  areaId: area.id,
});

export const reportRohcOfficeToDto = (office: ReportRohcOffice): ReportRohcOfficeDto => ({
  ...cast<ReportRohcOfficeDto>(omit(office, 'entityState', 'areaId')),
});

export const reportRohcAreaToDto = (area: ReportRohcArea): ReportRohcAreaDto => ({
  ...area,
  id: isDirtyTypeNew(area.dirtyType) ? <any>void 0 : area.id,
  details: [],
});
export const reportRohcAreaFromDto = (area: ReportRohcAreaDto): ReportRohcArea =>
  <ReportRohcAreaDto>omit(area, 'details');

export const reportRohcMasterCcFromDto = (masterCc: ReportRohcMasterCostCenterDto): ReportRohcMasterCc =>
  <ReportRohcMasterCc>omit(masterCc, 'details');
export const reportRohcMasterCcToDto = (masterCc: ReportRohcMasterCc): ReportRohcMasterCostCenterDto => ({
  ...masterCc,
  id: isDirtyTypeNew(masterCc.dirtyType) ? <any>void 0 : masterCc.id,
  details: [],
});

export const reportRohcOfficeCcFromDto = (masterCc: ReportRohcMasterCostCenterDto,
                                          officeCc: ReportRohcOfficeCostCenterDto): ReportRohcOfficeCc => ({
  ...officeCc,
  masterId: masterCc.id,
});
export const reportRohcOfficeCcToDto = (officeCc: ReportRohcOfficeCc): ReportRohcOfficeCostCenterDto =>
  <ReportRohcOfficeCostCenterDto>omit(officeCc, 'masterId');

export const reportRohcDetailsFromDto = (dto: ReportRohcDetailsDto): ReportRohcDetails => {
  const { areas, masterCostCenters } = dto;
  const areaOffices = flatMap(areas.map((area) =>
    area.details.map((office) => reportRohcOfficeFromDto(area, office))));
  const officeCostCenters = flatMap(masterCostCenters.map((masterCc) =>
    masterCc.details.map((officeCc) => reportRohcOfficeCcFromDto(masterCc, officeCc))));
  return {
    areas: reportRohcAreaTable.create(arrayIterator(areas.map(reportRohcAreaFromDto))),
    areaOffices: reportRohcOfficeTable.create(arrayIterator(areaOffices)),
    masterCostCenters: reportRohcMasterCcTable.create(arrayIterator(masterCostCenters.map(reportRohcMasterCcFromDto))),
    officeCostCenters: reportRohcOfficeCcTable.create(arrayIterator(officeCostCenters)),
    selectedTab: ReportRohcDetailsTab.Areas,
    selectedAreaId: maybeMap(first(dto.areas), getReportRohcAreaId),
    layouts: {},
  };
};

export const reportRohcDetailsToDto = (details: ReportRohcDetails): ReportRohcDetailsDto => ({
  areas: reportRohcAreaTable.all(details.areas).map((area) => ({
    ...reportRohcAreaToDto(area),
    details: reportRohcOfficeTable.byGroup(details.areaOffices, 'area-all', area.id).map(reportRohcOfficeToDto),
  })),
  masterCostCenters: reportRohcMasterCcTable.all(details.masterCostCenters).map((masterCc) => ({
    ...reportRohcMasterCcToDto(masterCc),
    details: reportRohcOfficeCcTable.byGroup(details.officeCostCenters, 'master-all', masterCc.id)
      .map(reportRohcOfficeCcToDto),
  })),
});

export const queryReportRohcAreas = ({ areas }: ReportRohcDetails) => reportRohcAreaTable.byOrder(areas, 'name');
export const queryReportRohcAllHousePlans = ({ areaOffices }: ReportRohcDetails): AutocompleteItem[] =>
  <AutocompleteItem[]>reportRohcOfficeTable.all(areaOffices).map((office) => office.housePlan).filter(Boolean);
export const queryReportRohcAllOffices = ({ areaOffices }: ReportRohcDetails) => reportRohcOfficeTable.all(areaOffices);
export const queryReportRohcAllOfficeCostCenters = ({ officeCostCenters }: ReportRohcDetails) =>
  reportRohcOfficeCcTable.all(officeCostCenters);
export const queryReportRohcCurrentOffices = ({ areaOffices, selectedAreaId }: ReportRohcDetails) =>
  selectedAreaId ? reportRohcOfficeTable.byGroup(areaOffices, 'area', selectedAreaId) : EMPTY_ARRAY;
export const queryReportRohcAreaById = ({ areas }: ReportRohcDetails, id: ID) => reportRohcAreaTable.byId(areas, id);

export const queryReportRohcMasterCcs = ({ masterCostCenters }: ReportRohcDetails) =>
  reportRohcMasterCcTable.byOrder(masterCostCenters, 'code');
export const queryReportRohcCurrentOfficeCcs = ({ officeCostCenters, selectedMasterCcId }: ReportRohcDetails) =>
  selectedMasterCcId ? reportRohcOfficeCcTable.byGroup(officeCostCenters, 'master', selectedMasterCcId) : EMPTY_ARRAY;
export const queryReportRohcMasterCcById = ({ masterCostCenters }: ReportRohcDetails, id: ID) =>
  reportRohcMasterCcTable.byId(masterCostCenters, id);
